import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { SvgTv } from '../../../../icons';
import { useSubscribedProductsForTitle } from '../../../../queries/ats/me/products/hooks';
import { SubscribedProduct } from '../../../../queries/ats/me/products/types';
import { useTitleProvider } from '../../../../queries/ats/me/providers/hooks';
import {
  ProgramProvider,
  Provider,
  SvodProvider,
  ThirdPartyProvider,
  VodProvider,
} from '../../../../queries/ats/me/providers/types';
import { Title } from '../../../../queries/ats/titles/types/title';
import { createSubscriptionStatus } from '../../../../utils/atsAssetUtils';
import { warn } from '../../../../utils/commonUtils';
import { Image } from '../../../image/Image';
import { useDontShowAgain } from '../../LinkModal/use-dont-show-again';
import css from './source-indicator.module.scss';

const cache = new Set<string>();

interface SourceIndicatorProps {
  title: Title;
}

export function SourceIndicator({ title }: SourceIndicatorProps) {
  const { provider, error, isLoading } = useTitleProvider(title);

  useEffect(() => {
    if (provider) {
      // Cache the provider id to prevent re-animations
      cache.add(provider.id);
    }
  }, [provider]);

  if (isLoading) {
    return null;
  }

  if (error) {
    return null;
  }

  if (!provider) {
    warn(`No provider found for title ${title.titleName} (${title.masterId})`);
    return null;
  }

  return <ProviderSourceIndicator provider={provider} title={title} />;
}

interface ProviderSourceIndicatorProps {
  provider: Provider;
  title: Title;
}

export function ProviderSourceIndicator({ provider, title }: ProviderSourceIndicatorProps) {
  switch (provider.type) {
    case 'program':
      return <ProgramSourceIndicator provider={provider} title={title} />;
    case 'svod':
      return <SvodSourceIndicator provider={provider} masterId={title.masterId} />;
    case 'thirdParty':
      return <ThirdPartySourceIndicator provider={provider} />;
    case 'vod':
      return <VodSourceIndicator provider={provider} masterId={title.masterId} price={title.price} />;
    default:
      return null;
  }
}

export function SourceIndicatorContainer({ provider, children }: { provider: Provider; children: React.ReactNode }) {
  return (
    <div className={css.container} data-animate={!cache.has(provider.id)}>
      {children}
    </div>
  );
}

function ProgramSourceIndicator({ provider, title }: { provider: ProgramProvider; title: Title }) {
  const { t } = useTranslation();
  const { data: products } = useSubscribedProductsForTitle(title.masterId);
  const isOwned = getIsOwnedStatus(products);
  const hasRecording = Boolean(title.recordings && title.recordings.length > 0);
  const hasAccess = Boolean(provider.isSubscribed || hasRecording);

  return (
    <SourceIndicatorContainer provider={provider}>
      {!hasAccess && !isOwned && <div className={css.text}>{t('get access')}</div>}
      <SvgTv height={24} width={24} className={css.logo} />
    </SourceIndicatorContainer>
  );
}

interface SvodSourceIndicatorProps {
  provider: SvodProvider;
  masterId: string | undefined;
}

export function SvodSourceIndicator({ provider, masterId }: SvodSourceIndicatorProps) {
  const { t } = useTranslation();

  const { data: products } = useSubscribedProductsForTitle(masterId);
  const isOwned = getIsOwnedStatus(products);

  return (
    <SourceIndicatorContainer provider={provider}>
      {!provider.isSubscribed && !isOwned && <div className={css.text}>{t('get access')}</div>}
      <Image className={css.logo} height={40} src={provider.image} />
    </SourceIndicatorContainer>
  );
}

export function ThirdPartySourceIndicator({ provider }: { provider: ThirdPartyProvider }) {
  const { t } = useTranslation();

  const { isChecked: modalIsDisabledByUser } = useDontShowAgain(provider.id);

  return (
    <SourceIndicatorContainer provider={provider}>
      {!provider.isSubscribed && !modalIsDisabledByUser && <div className={css.text}>{t('get access')}</div>}
      <Image className={css.logo} height={40} src={provider.image} />
    </SourceIndicatorContainer>
  );
}

interface VodSourceIndicatorProps {
  provider: VodProvider;
  masterId: string;
  price: number | null;
}

function VodSourceIndicator({ provider, masterId, price }: VodSourceIndicatorProps) {
  const { t } = useTranslation();

  const { data: products } = useSubscribedProductsForTitle(masterId);
  const isOwned = getIsOwnedStatus(products);

  return (
    <SourceIndicatorContainer provider={provider}>
      {!isOwned && <div className={css.text}>{price ? `${price},-` : t('get access')}</div>}
      <Image className={css.logo} height={40} src={provider.image} />
    </SourceIndicatorContainer>
  );
}

function getIsOwnedStatus(products: SubscribedProduct[] | undefined) {
  if (!products) {
    return false;
  }

  const product = products?.[0];

  const subscriptionStatus = product ? createSubscriptionStatus(product) : undefined;
  return subscriptionStatus?.status === 'rented' || subscriptionStatus?.status === 'bought';
}
